// ============================================================
// CineSync — Admin Invitation Management Modal
// Allows the site administrator to generate single-use invites,
// view status, set expiration, and revoke invitations or host access.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  KeyRound,
  Plus,
  Copy,
  Check,
  RefreshCw,
  Clock,
  Shield,
  ShieldAlert,
  ShieldX,
  AlertCircle,
  Loader2,
  Trash2,
  UserCheck,
  ExternalLink,
} from 'lucide-react';
import {
  adminListInvitations,
  adminCreateInvitation,
  adminRevokeInvitation,
  adminRevokeHostApproval,
  type AdminInvitationItem,
} from '@/services/invitationService';
import { useAuth } from '@/hooks/useAuth';

interface CachedAdminInvite {
  token: string;
  tokenHash: string;
  expiresAt: number | null;
  createdAt: number;
}

const ADMIN_INVITES_CACHE_KEY = 'cinesync_admin_invitations_cache';

function getLocalCachedInvites(): Record<string, CachedAdminInvite> {
  try {
    const raw = localStorage.getItem(ADMIN_INVITES_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveLocalCachedInvite(item: CachedAdminInvite): void {
  try {
    const cache = getLocalCachedInvites();
    cache[item.tokenHash] = item;
    localStorage.setItem(ADMIN_INVITES_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function removeLocalCachedInvite(tokenHash: string): void {
  try {
    const cache = getLocalCachedInvites();
    delete cache[tokenHash];
    localStorage.setItem(ADMIN_INVITES_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

interface AdminInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdminInviteModal({ isOpen, onClose }: AdminInviteModalProps) {
  const { isAdmin } = useAuth();

  const [invitations, setInvitations] = useState<AdminInvitationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cached invitations in localStorage so generated links survive app switches
  const [cachedTokens, setCachedTokens] = useState<Record<string, CachedAdminInvite>>(() => getLocalCachedInvites());
  const [copiedHash, setCopiedHash] = useState<string | null>(null);

  // Invitation creation state (persists recent active token across app switches)
  const [expirationHours, setExpirationHours] = useState<number | null>(72); // Default 3 days
  const [isCreating, setIsCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(() => {
    const cache = getLocalCachedInvites();
    const items = Object.values(cache).sort((a, b) => b.createdAt - a.createdAt);
    if (items.length > 0 && Date.now() - items[0].createdAt < 4 * 3600 * 1000) {
      if (!items[0].expiresAt || items[0].expiresAt > Date.now()) {
        return items[0].token;
      }
    }
    return null;
  });
  const [hasCopied, setHasCopied] = useState(false);

  // Revoking state
  const [revokingHash, setRevokingHash] = useState<string | null>(null);
  const [revokingUid, setRevokingUid] = useState<string | null>(null);

  const fetchInvitations = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const list = await adminListInvitations();
      setInvitations(list);
      setCachedTokens(getLocalCachedInvites());
    } catch (err: any) {
      setError(err.message || 'Failed to load invitations.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isOpen && isAdmin) {
      fetchInvitations();
    }
  }, [isOpen, isAdmin, fetchInvitations]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    setCreatedToken(null);
    setHasCopied(false);

    try {
      const res = await adminCreateInvitation(expirationHours);
      setCreatedToken(res.token);
      const cachedItem: CachedAdminInvite = {
        token: res.token,
        tokenHash: res.tokenHash,
        expiresAt: res.expiresAt,
        createdAt: Date.now(),
      };
      saveLocalCachedInvite(cachedItem);
      setCachedTokens(getLocalCachedInvites());
      await fetchInvitations();
    } catch (err: any) {
      setError(err.message || 'Failed to create invitation.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = () => {
    if (!createdToken) return;
    const url = `${window.location.origin}/invite/${createdToken}`;
    navigator.clipboard.writeText(url);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2500);
  };

  const handleCopyCachedLink = (token: string, hash: string) => {
    const url = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedHash(hash);
    setTimeout(() => setCopiedHash(null), 2500);
  };

  const handleRevokeInvite = async (tokenHash: string) => {
    if (!window.confirm('Are you sure you want to revoke this invitation? It will no longer be redeemable.')) {
      return;
    }

    setRevokingHash(tokenHash);
    try {
      const ok = await adminRevokeInvitation(tokenHash);
      if (ok) {
        removeLocalCachedInvite(tokenHash);
        setCachedTokens(getLocalCachedInvites());
        if (createdToken && cachedTokens[tokenHash]?.token === createdToken) {
          setCreatedToken(null);
        }
        await fetchInvitations();
      } else {
        alert('Could not revoke invitation.');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to revoke.');
    } finally {
      setRevokingHash(null);
    }
  };

  const handleRevokeUserHost = async (targetUid: string, email?: string | null) => {
    if (
      !window.confirm(
        `Are you sure you want to revoke host approval for ${email || targetUid}? They will no longer be able to create watch parties.`
      )
    ) {
      return;
    }

    setRevokingUid(targetUid);
    try {
      const ok = await adminRevokeHostApproval(targetUid);
      if (ok) {
        await fetchInvitations();
      } else {
        alert('Could not revoke host approval.');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to revoke host status.');
    } finally {
      setRevokingUid(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto p-4 py-6 bg-black/80 backdrop-blur-sm animate-fade-in font-sans contain-overscroll">
      <div className="relative w-full max-w-2xl bg-cinema-900 border border-cinema-700/80 rounded-xl shadow-cinema-screen text-cinema-100 flex flex-col my-auto contain-overscroll">
        {/* Header */}
        <div className="px-6 py-4 hairline-b flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-lamp-dim border border-lamp/30 flex items-center justify-center text-lamp">
              <KeyRound className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-bold text-cinema-100 tracking-tight flex items-center gap-2">
                <span>Invitation Management</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-lamp/15 text-lamp border border-lamp/30 font-semibold uppercase">
                  Admin
                </span>
              </h2>
              <p className="text-xs text-cinema-400">
                Generate single-use links for friends to create host accounts.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchInvitations}
              disabled={loading}
              className="p-1.5 rounded-lg text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
              title="Refresh invitations"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {error && (
            <div className="p-3 rounded-lg bg-red-950/50 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Section: Generate New Invitation */}
          <div className="p-4 rounded-xl bg-cinema-950 border border-cinema-750 space-y-3.5">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-cinema-100 flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5 text-lamp" />
                <span>Create New Invitation Link</span>
              </h3>
              <span className="text-[11px] text-cinema-400">One-time invitation link</span>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-2 space-y-1">
                <label className="block text-[11px] font-medium text-cinema-400">
                  Link Expiration:
                </label>
                <div className="grid grid-cols-4 gap-1.5 p-1 bg-cinema-900 rounded-lg border border-cinema-700">
                  <button
                    type="button"
                    onClick={() => setExpirationHours(24)}
                    className={`py-1.5 text-xs font-medium rounded transition-colors ${
                      expirationHours === 24
                        ? 'bg-lamp text-cinema-950 font-bold'
                        : 'text-cinema-300 hover:text-cinema-100'
                    }`}
                  >
                    24h
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpirationHours(72)}
                    className={`py-1.5 text-xs font-medium rounded transition-colors ${
                      expirationHours === 72
                        ? 'bg-lamp text-cinema-950 font-bold'
                        : 'text-cinema-300 hover:text-cinema-100'
                    }`}
                  >
                    3 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpirationHours(168)}
                    className={`py-1.5 text-xs font-medium rounded transition-colors ${
                      expirationHours === 168
                        ? 'bg-lamp text-cinema-950 font-bold'
                        : 'text-cinema-300 hover:text-cinema-100'
                    }`}
                  >
                    7 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpirationHours(null)}
                    className={`py-1.5 text-xs font-medium rounded transition-colors ${
                      expirationHours === null
                        ? 'bg-lamp text-cinema-950 font-bold'
                        : 'text-cinema-300 hover:text-cinema-100'
                    }`}
                  >
                    Never
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full py-2.5 px-3 rounded-lg bg-lamp hover:bg-lamp-bright active:scale-[0.98] disabled:opacity-50 text-cinema-950 font-bold text-xs tracking-wide transition-all flex items-center justify-center gap-1.5 shadow-sm"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Generating…</span>
                  </>
                ) : (
                  <>
                    <KeyRound className="w-3.5 h-3.5" />
                    <span>Generate Link</span>
                  </>
                )}
              </button>
            </div>

            {/* Generated Link Box */}
            {createdToken && (
              <div className="mt-3 p-3.5 rounded-lg bg-lamp/10 border border-lamp/30 space-y-2 animate-fade-in">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-lamp flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    <span>Invitation Link Generated!</span>
                  </span>
                  <span className="text-[11px] text-cinema-400">
                    Expires:{' '}
                    {expirationHours ? `in ${expirationHours / 24} days` : 'Never'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/invite/${createdToken}`}
                    className="flex-1 bg-cinema-900 border border-cinema-700 rounded px-2.5 py-1.5 text-xs font-mono text-cinema-100 select-all"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="px-3 py-1.5 rounded bg-lamp hover:bg-lamp-bright active:scale-[0.98] text-cinema-950 font-bold text-xs flex items-center gap-1.5 transition-all"
                  >
                    {hasCopied ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy Link</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-cinema-400">
                  Share this link with a friend. Once they register or redeem it, it is permanently consumed.
                </p>
              </div>
            )}
          </div>

          {/* Section: Existing Invitations List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-cinema-300">
                All Invitations ({invitations.length})
              </h3>
              {loading && <Loader2 className="w-3.5 h-3.5 text-lamp animate-spin" />}
            </div>

            {invitations.length === 0 ? (
              <div className="p-8 text-center bg-cinema-950 rounded-xl border border-cinema-800 text-cinema-400 text-xs">
                {loading ? 'Loading invitations…' : 'No invitations created yet. Generate one above.'}
              </div>
            ) : (
              <div className="space-y-2">
                {invitations.map((inv, idx) => {
                  const isRevokingThis = revokingHash === inv.tokenHash;
                  const isRevokingThisUid = inv.usedByUid && revokingUid === inv.usedByUid;

                  let badgeColor = 'bg-cinema-800 text-cinema-300 border-cinema-700';
                  let statusText: string = inv.status;

                  if (inv.status === 'active') {
                    badgeColor = 'bg-reel-emerald/20 text-reel-emerald border-reel-emerald/40';
                    statusText = 'Active (Unused)';
                  } else if (inv.status === 'used') {
                    badgeColor = 'bg-blue-500/20 text-blue-300 border-blue-500/30';
                    statusText = 'Redeemed';
                  } else if (inv.status === 'expired') {
                    badgeColor = 'bg-amber-500/20 text-amber-300 border-amber-500/30';
                    statusText = 'Expired';
                  } else if (inv.status === 'revoked') {
                    badgeColor = 'bg-red-500/20 text-red-300 border-red-500/30';
                    statusText = 'Revoked';
                  }

                  return (
                    <div
                      key={inv.tokenHash}
                      className="p-3.5 rounded-lg bg-cinema-950 border border-cinema-750 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${badgeColor}`}
                          >
                            {statusText}
                          </span>
                          <span className="text-cinema-400 text-[11px] font-medium">
                            Invite #{invitations.length - idx}
                          </span>
                        </div>

                        <div className="text-[11px] text-cinema-400 flex items-center gap-3 flex-wrap">
                          <span>
                            Created:{' '}
                            <strong className="text-cinema-300 font-normal">
                              {new Date(inv.createdAt).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </strong>
                          </span>

                          <span>
                            Expires:{' '}
                            <strong className="text-cinema-300 font-normal">
                              {inv.expiresAt
                                ? new Date(inv.expiresAt).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'Never'}
                            </strong>
                          </span>
                        </div>

                        {inv.status === 'used' && (
                          <div className="text-[11px] text-blue-300/90 pt-0.5">
                            Redeemed by:{' '}
                            <span className="font-medium text-cinema-100">
                              {inv.usedByEmail || inv.usedByUid}
                            </span>
                            {inv.usedAt && (
                              <span className="text-cinema-500 ml-1">
                                (
                                {new Date(inv.usedAt).toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                                )
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                        {inv.status === 'active' && cachedTokens[inv.tokenHash] && (
                          <button
                            type="button"
                            onClick={() => handleCopyCachedLink(cachedTokens[inv.tokenHash].token, inv.tokenHash)}
                            className="px-2.5 py-1 rounded bg-lamp/15 hover:bg-lamp/25 text-lamp border border-lamp/30 text-[11px] font-medium transition-colors flex items-center gap-1 active:scale-95"
                            title="Copy invitation link"
                          >
                            {copiedHash === inv.tokenHash ? (
                              <>
                                <Check className="w-3 h-3 text-lamp-bright" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copy Link</span>
                              </>
                            )}
                          </button>
                        )}

                        {inv.status === 'active' && (
                          <button
                            type="button"
                            onClick={() => handleRevokeInvite(inv.tokenHash)}
                            disabled={isRevokingThis}
                            className="px-2.5 py-1 rounded bg-cinema-850 hover:bg-red-500/20 hover:text-red-300 border border-cinema-700 hover:border-red-500/30 text-cinema-400 text-[11px] font-medium transition-colors flex items-center gap-1"
                          >
                            {isRevokingThis ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                            <span>Revoke</span>
                          </button>
                        )}

                        {inv.status === 'used' && inv.usedByUid && (
                          <button
                            type="button"
                            onClick={() => handleRevokeUserHost(inv.usedByUid!, inv.usedByEmail)}
                            disabled={Boolean(isRevokingThisUid)}
                            className="px-2.5 py-1 rounded bg-cinema-850 hover:bg-red-500/20 hover:text-red-300 border border-cinema-700 hover:border-red-500/30 text-cinema-400 text-[11px] font-medium transition-colors flex items-center gap-1"
                            title="Revoke host privileges for this user"
                          >
                            {isRevokingThisUid ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <ShieldX className="w-3 h-3" />
                            )}
                            <span>Revoke Host Access</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 hairline-t bg-cinema-950/80 flex items-center justify-between text-xs text-cinema-400">
          <span>WatchParty Invite-Only Security Architecture</span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-cinema-100 font-medium text-xs transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
